import './ConsentStepRibbon.css';

/**
 * 7-step indicator for the parental-consent flow. Used across all
 * consent screens (Steps 1–7). One of three parent-distinctive
 * design moves per the plan: a unique reassurance widget so the
 * parent always knows where they are.
 *
 * States:
 *   - active: current step (filled accent dot)
 *   - completed: prior step (smaller filled dot, lower opacity)
 *   - upcoming: hollow ring at low chroma
 *
 * Pure presentational. Step labels are passed in so the consent
 * flow's copy stays in one place.
 */

export type ConsentStepNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7;

const STEP_LABELS: Record<ConsentStepNumber, string> = {
  1: "Child's date of birth",
  2: "Parent's email",
  3: 'Email sent',
  4: 'Privacy notice',
  5: 'Identity verification',
  6: 'Consent choices',
  7: 'Confirm',
};

export interface ConsentStepRibbonProps {
  active: ConsentStepNumber;
}

export function ConsentStepRibbon({ active }: ConsentStepRibbonProps) {
  const steps: ConsentStepNumber[] = [1, 2, 3, 4, 5, 6, 7];
  return (
    <ol className="consent-ribbon" aria-label={`Step ${active} of 7: ${STEP_LABELS[active]}`}>
      {steps.map((step, idx) => {
        const state = step === active ? 'active' : step < active ? 'completed' : 'upcoming';
        const isLast = idx === steps.length - 1;
        return (
          <li
            key={step}
            className="consent-ribbon__item"
            data-state={state}
            aria-current={state === 'active' ? 'step' : undefined}
          >
            <span className="consent-ribbon__dot">
              <span className="consent-ribbon__num">{step}</span>
            </span>
            <span className="consent-ribbon__label">{STEP_LABELS[step]}</span>
            {!isLast && <span className="consent-ribbon__line" aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}
